import {
  EmptyInput,
  Flex,
  Icon,
  InfoItemContainer,
  InputGroup,
  InputRow,
  ModelNewNumericInput,
  SVGInfo,
} from '@equifinance/shared';
import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAccountContext } from '../../../../contexts';
import { PaddedSection } from '../../components/Section';
import { InfoListItemAction } from '../../../Errors/PageErrors';

export const MonthlyExpenditureRefName = 'monthly-expenditure';

type InputTypes = 'EssentialExpenditure' | 'CouncilTax' | 'Insurances' | 'TransportExpenditure' | 'NonEssentialExpenditure';

function ExpenditureInput({ inputType }: { inputType: InputTypes | 'Empty' }) {
  const { account } = useAccountContext();

  const {
    expenditure,
  } = account;

  const {
    essentialExpenditureTooltip = [],
    nonEssentialExpenditureTooltip = [],
  } = expenditure;

  const typeToProps: Record<InputTypes, {
    label: string,
    field: string,
    tooltip?: ReactNode,
  }> = {
    EssentialExpenditure: {
      label: 'Essential expenditure',
      field: 'expenditure.essentialExpenditure',
      tooltip: (
        <>
          {!!essentialExpenditureTooltip?.length && (
            <>
              Includes:
              <ul>
                {essentialExpenditureTooltip?.map((tip) => (
                  <li>{tip}</li>
                ))}
              </ul>
            </>
          )}

        </>),
    },
    NonEssentialExpenditure: {
      label: 'Non-essential expenditure',
      field: 'expenditure.nonEssentialExpenditure',
      tooltip: (
        <>
          {!!nonEssentialExpenditureTooltip?.length && (
            <>
              Includes:
              <ul>
                {nonEssentialExpenditureTooltip?.map((tip) => (
                  <li>{tip}</li>
                ))}
              </ul>
            </>
          )}

        </>),
    },
    CouncilTax: {
      label: 'Council tax',
      field: 'expenditure.councilTax',
    },
    Insurances: {
      label: 'Insurances',
      field: 'expenditure.insurances',
      tooltip: (
        <>
          Includes:
          <ul>
            <li>Buildings</li>
            <li>Contents</li>
            <li>Life</li>
            <li>Pet</li>
            <li>Health</li>
            <li>Appliance/Gadget</li>
          </ul>
          Note: Vehicle insurance is to be included under transport expenditure
        </>
      ),
    },
    TransportExpenditure: {
      label: 'Transport expenditure',
      field: 'expenditure.transportExpenditure',
      tooltip: 'Costs associated with regular transport e.g. train tickets, taxis/uber, parking fuel, vehicle insurance, MOT, vehicle maintenance/servicing',
    },
  };

  if (inputType === 'Empty') {
    return <EmptyInput />;
  }

  return (
    <ModelNewNumericInput
      {...typeToProps[inputType]}
      prefix="£"
      isFixedDecimal
      required
      maxCharacters={10}
    />
  );
}
const Info = styled.div`${({ theme }) => `
  border-radius: ${theme.borderRadius[2]};
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.06), 0px 1px 3px 0px rgba(16, 24, 40, 0.1);

  ${InfoItemContainer} {
    margin: 0;
  }

  a {
    text-decoration: none;
    color: ${theme.textPrimary};
  }
`}`;

const InfoContainer = styled(Flex)`${({ theme }) => `
  justify-content: space-between;
  min-height: ${theme.padding[6]};
  align-items: stretch;
  line-height ${theme.padding[6]};
`}`;

export default function MonthlyExpenditureSection() {
  const { account } = useAccountContext();
  const allowedExpenditureFields = ['EssentialExpenditure', 'CouncilTax', 'Insurances', 'TransportExpenditure', 'NonEssentialExpenditure'];

  const {
    expenditure,
    errors,
    warnings,
  } = account;

  const {
    enabledExpenditureFields = [],
  } = expenditure;

  const expenditureFields = enabledExpenditureFields.filter((ef) => allowedExpenditureFields.includes(ef));

  const chunkArray = (arr: string[], size: number) => {
    const result = [];
    for (let i = 0; i < arr.length; i += size) {
      result.push(arr.slice(i, i + size));
    }
    return result;
  };

  const inputs = chunkArray(expenditureFields, 2);

  if (expenditureFields.length % 2 !== 0) {
    inputs[inputs.length - 1].push('Empty');
  }

  return (
    <PaddedSection
      id={expenditure?.id}
      refName={MonthlyExpenditureRefName}
      title="Monthly Expenditure"
      errors={errors}
      warnings={warnings}
    >
      <Info>
        <SVGInfo>
          <InfoContainer>
            For additional information on providing Monthly Expenditure details please see our help page
            <Link to={`/help/monthly-expenditure/${expenditure.version}`} target="_blank">
              <InfoListItemAction>
                <Icon className="mi-circle-arrow-right" />
                Go to page
              </InfoListItemAction>
            </Link>
          </InfoContainer>
        </SVGInfo>
      </Info>
      <InputGroup>
        {inputs.map((inputRow) => (
          <InputRow>
            {inputRow.map((inputType) => (
              <ExpenditureInput
                inputType={inputType as InputTypes}
              />
            ))}
          </InputRow>
        ))}
      </InputGroup>
    </PaddedSection>
  );
}
